import { FormDescription, FormItem, FormMessage } from "~/components/ui/form";
import { FormLabel, Label } from "~/components/ui-custom/label";
import { FormControl } from "~/components/ui/form";
import type { ControllerRenderProps, UseFormReturn } from "react-hook-form";
import { FormField } from "~/components/ui/form";
import { Input, Textarea } from "~/components/ui-custom/input";
import { RankingInput } from "~/components/ui-custom/ranking-input";
import { cn } from "../utils";
import {
  testFieldAgainstAllDependencies,
  testFieldAgainstDependencyOperator,
  type FormKeys,
  type ObjectConfigValues,
} from "./form-config-types";
import type { FormConfig, I18NFunction } from "./form-config-types";
import { RadioGroup } from "~/components/ui/radio-group";
import { RadioGroupItem } from "~/components/ui-custom/radio-group";
import { z } from "zod";
import { Checkbox } from "~/components/ui-custom/checkbox";
import { match } from "ts-pattern";
import { getFieldsWithDependencies } from "./data/form-data-utils";
import { P2, P3 } from "~/components/ui-custom/text";
import { Switch } from "~/components/ui/switch";
import { useI18n } from "~/hooks/use-i18n";
import { renderLabel } from "~/components/MarkdownLabel";

/**
 * This function takes a zod object and for each field, it returns a form component.
 *
 * If the field is a string, it returns a text input.
 * If the field is a boolean, it returns a checkbox.
 * If the field is an enum, it returns a select.
 * If the field is an object, it returns a nested form.
 * If the field is an array, it returns a list of the same form component.
 */
export function formConfigToFormComponents<K extends string>({
  form,
  config,
  customComponents,
  l,
}: {
  form: UseFormReturn<any>;
  config: FormConfig;
  customComponents?: Partial<
    Record<"text" | "textarea" | "switch", (field: ControllerRenderProps<any, any>) => React.ReactNode>
  >;
  l: I18NFunction;
}): Record<K, React.ReactNode> {
  let formComponents: Record<K, React.ReactNode> = {} as Record<K, React.ReactNode>;

  const watchedFields = getFieldsWithDependencies({ config });
  const watchedValues = form.watch(watchedFields);
  // map Record<watchedFields, watchedValues>
  // const watchedValuesMap = Object.fromEntries(watchedFields.map((field, index) => [field, watchedValues[index]]));

  // console.log(watchedValuesMap);

  function getFormComponent({
    configKey,
    config,
    objectHasNotApplicableOption,
    objectHasNotApplicableChecked,
  }: {
    configKey: string;
    config: ObjectConfigValues;
    objectHasNotApplicableOption?: boolean;
    objectHasNotApplicableChecked?: boolean;
  }) {
    //
    // unwrap, if necessary
    //
    let schema = config.schema;
    let propertyHasNotApplicableOption = false;

    // console.log("schema", schema);
    // Now we can safely unwrap the schema
    if (schema instanceof z.ZodEffects) {
      //
      // zod effects
      //
      schema = (schema as z.ZodEffects<any>).innerType();
    } else if (schema instanceof z.ZodOptional) {
      //
      // zod optional
      //
      schema = (schema as z.ZodOptional<any>).unwrap();
      if (schema instanceof z.ZodNullable) {
        schema = (schema as z.ZodNullable<any>).unwrap();
      }
    } else if (schema instanceof z.ZodNullable) {
      //
      // zod nullable
      //
      schema = (schema as z.ZodNullable<any>).unwrap();
    } else if (schema instanceof z.ZodUnion) {
      //
      // zod union of enum and something else
      //
      if (schema.options[0]) {
        const notApplicableSchema = schema.options[1];
        schema = schema.options[0];

        if (notApplicableSchema instanceof z.ZodLiteral && notApplicableSchema.value === "not_applicable") {
          propertyHasNotApplicableOption = true;
        }
      }
    }

    //
    // check if the field should be hidden
    //
    // if (config.hideIf) {
    //   const [pathToDependency, operator, valueToCheckAgainst] = config.hideIf;
    //   const indexOfDependency = watchedFields.indexOf(pathToDependency);

    //   const dependencyValue = watchedValues[indexOfDependency];

    //   const doesFieldPassDependencyCheck = testFieldAgainstDependencyOperator({
    //     operator,
    //     valueToCheckAgainst,
    //     dependencyValue,
    //   });

    //   // return if the field should be hidden
    //   if (doesFieldPassDependencyCheck) {
    //     return;
    //   }
    // }

    //
    // check if the field should be hidden
    //
    if (config.dependencies) {
      const doesFieldPassAllDependencies = testFieldAgainstAllDependencies({
        testFor: "hide",
        dependencies: config.dependencies,
        getDependencyValue: (pathToDependency: string) => {
          // console.log(
          //   "pathToDependency",
          //   pathToDependency,
          //   "watchedValues[watchedFields.indexOf(pathToDependency)]",
          //   watchedValues[watchedFields.indexOf(pathToDependency)]
          // );
          return watchedValues[watchedFields.indexOf(pathToDependency)];
        },
      });

      // console.log(configKey, "doesFieldPassAllDependencies", doesFieldPassAllDependencies);

      if (doesFieldPassAllDependencies) {
        return;
      }
    }

    const fieldValueIsNotApplicable = (fieldValue: any) => fieldValue === "not_applicable";

    match(config)
      .with({ type: "text" }, (config) => {
        formComponents[configKey as K] = (
          <FormField
            control={form.control}
            name={configKey as any}
            render={({ field }) =>
              objectHasNotApplicableOption &&
              objectHasNotApplicableChecked &&
              fieldValueIsNotApplicable(field.value) ? (
                <></>
              ) : (
                <FormItem className={cn(config.formItemClassName)}>
                  {config.label && <FormLabel>{renderLabel(config.label)}</FormLabel>}
                  {config.description && <FormDescription>{config.description}</FormDescription>}
                  <FormControl>
                    {customComponents?.["text"]?.(field) ?? (
                      <Input {...field} value={field.value ?? ""} placeholder={config.placeholder} />
                    )}
                  </FormControl>
                  {config.helpText && <P3 gray>{config.helpText}</P3>}
                  <FormMessage />
                </FormItem>
              )
            }
          />
        );
      })
      .with({ type: "textarea" }, (config) => {
        // const indexOfNotApplicable = watchedFields.indexOf(`${key}NotApplicable`);
        // const notApplicableValue = watchedValues[indexOfNotApplicable];
        formComponents[configKey as K] = (
          <FormField
            control={form.control}
            name={configKey as any}
            render={({ field }) =>
              objectHasNotApplicableOption &&
              objectHasNotApplicableChecked &&
              fieldValueIsNotApplicable(field.value) ? (
                <></>
              ) : (
                <FormItem className={cn(config.formItemClassName)}>
                  {config.label && <FormLabel>{renderLabel(config.label)}</FormLabel>}
                  {config.description && <FormDescription>{config.description}</FormDescription>}
                  <div className="space-y-2">
                    {propertyHasNotApplicableOption && !config.hideNotApplicableCheckbox && (
                      <NotApplicableCheckbox field={field} configKey={configKey} />
                    )}

                    {propertyHasNotApplicableOption && fieldValueIsNotApplicable(field.value) ? null : (
                      <FormControl>
                        {customComponents?.["textarea"]?.(field) ?? (
                          <Textarea
                            {...field}
                            value={field.value ?? ""}
                            placeholder={config.placeholder || l("Enter answer here...", "Saisir la réponse ici...")}
                          />
                        )}
                      </FormControl>
                    )}
                    {config.helpText && <P3 gray>{config.helpText}</P3>}
                  </div>
                  <FormMessage />
                </FormItem>
              )
            }
          />
        );
      })
      .with({ type: "radioGroup" }, (config) => {
        // const indexOfNotApplicable = watchedFields.indexOf(`${key}NotApplicable`);
        // const notApplicableValue = watchedValues[indexOfNotApplicable];

        formComponents[configKey as K] = (
          <FormField
            control={form.control}
            name={configKey as any}
            render={({ field }) =>
              objectHasNotApplicableOption &&
              objectHasNotApplicableChecked &&
              fieldValueIsNotApplicable(field.value) ? (
                <></>
              ) : (
                <FormItem className={""}>
                  {config.label && <FormLabel>{renderLabel(config.label)}</FormLabel>}
                  {config.description && <FormDescription>{config.description}</FormDescription>}

                  {propertyHasNotApplicableOption && !config.hideNotApplicableCheckbox && (
                    <NotApplicableCheckbox field={field} configKey={configKey} />
                  )}

                  {propertyHasNotApplicableOption && fieldValueIsNotApplicable(field.value) ? null : (
                    <>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className={cn(
                            //
                            "gap-2",
                          )}
                          style={{
                            gridTemplateColumns: `repeat(${config.cols ?? 1}, 1fr)`,
                          }}
                        >
                          {/* loop over the options */}
                          {(schema as z.ZodEnum<any>).options.map((option: any) => {
                            const isChecked = field.value === option;
                            return (
                              <FormItem key={option}>
                                <FormLabel
                                  className={cn(
                                    //
                                    "flex items-start space-x-3 border border-gray-300 rounded-full p-3",
                                    "hover:bg-gray-50 cursor-pointer",
                                    isChecked && "bg-secondary-700/5 border-secondary-700 hover:bg-secondary-700/10",
                                  )}
                                >
                                  <FormControl>
                                    <RadioGroupItem value={option} />
                                  </FormControl>

                                  <P2>{config.mapToLabels ? config.mapToLabels(option, l) : option}</P2>
                                  {/* <P3>"{option}"</P3> */}
                                </FormLabel>
                              </FormItem>
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                      {config.helpText && <P3 gray>{config.helpText}</P3>}
                    </>
                  )}
                  <FormMessage />
                </FormItem>
              )
            }
          />
        );
      })
      .with({ type: "checkboxGroup" }, (config) => {
        formComponents[configKey as K] = (
          <FormField
            control={form.control}
            name={configKey as any}
            render={({ field }) =>
              objectHasNotApplicableOption &&
              objectHasNotApplicableChecked &&
              fieldValueIsNotApplicable(field.value) ? (
                <></>
              ) : (
                <FormItem>
                  <div className="mb-2">
                    {config.label && <FormLabel>{renderLabel(config.label)}</FormLabel>}
                    {config.description && <FormDescription>{config.description}</FormDescription>}
                  </div>

                  {propertyHasNotApplicableOption && !config.hideNotApplicableCheckbox && (
                    <NotApplicableCheckbox field={field} configKey={configKey} />
                  )}

                  {propertyHasNotApplicableOption && fieldValueIsNotApplicable(field.value) ? null : (
                    <>
                      {/* loop over the options */}
                      {(schema as z.ZodArray<z.ZodEnum<any>>).element.options.map((option: any) => (
                        <FormField
                          key={option}
                          control={form.control}
                          name={configKey as any}
                          render={({ field }) => {
                            const isChecked = field.value?.includes(option);
                            return (
                              <FormItem key={option}>
                                <FormLabel
                                  className={cn(
                                    //
                                    "flex items-start space-x-3 border border-gray-300 rounded-md p-3",
                                    "hover:bg-gray-50 cursor-pointer",
                                    isChecked && "bg-secondary-700/5 border-secondary-700 hover:bg-secondary-700/10",
                                  )}
                                >
                                  <FormControl>
                                    <Checkbox
                                      checked={isChecked}
                                      onCheckedChange={(checked) => {
                                        return checked
                                          ? field.onChange([...(field.value ?? []), option])
                                          : field.onChange(field.value?.filter((value: any) => value !== option));
                                      }}
                                    />
                                  </FormControl>

                                  <P2>{config.mapToLabels ? config.mapToLabels(option, l) : option}</P2>
                                </FormLabel>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                      {config.helpText && <P3 gray>{config.helpText}</P3>}
                    </>
                  )}
                  <FormMessage />
                </FormItem>
              )
            }
          />
        );
      })
      .with({ type: "switch" }, (config) => {
        formComponents[configKey as K] = (
          <FormField
            control={form.control}
            name={configKey as any}
            render={({ field }) => (
              <FormItem>
                {config.label && <FormLabel>{renderLabel(config.label)}</FormLabel>}
                {config.description && <FormDescription>{config.description}</FormDescription>}
                <FormControl>
                  {customComponents?.["switch"]?.(field) ?? (
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  )}
                </FormControl>
                {config.helpText && <P3 gray>{config.helpText}</P3>}
                <FormMessage />
              </FormItem>
            )}
          />
        );
      })
      .with({ type: "singleCheckbox" }, (config) => {
        formComponents[configKey as K] = (
          <FormField
            control={form.control}
            name={configKey as any}
            render={({ field }) => (
              <FormItem>
                <div className="flex items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    {config.label && <FormLabel>{renderLabel(config.label)}</FormLabel>}
                    {config.description && <FormDescription>{config.description}</FormDescription>}
                  </div>
                </div>
                {config.helpText && <P3 gray>{config.helpText}</P3>}
                <FormMessage />
              </FormItem>
            )}
          />
        );
      })
      .with({ type: "hidden" }, (config) => {
        formComponents[configKey as K] = (
          <FormField
            control={form.control}
            name={configKey as any}
            render={({ field }) => <Input {...field} type="hidden" className="absolute opacity-0" />}
          />
        );
      })
      .with({ type: "markdown" }, (config) => {
        formComponents[configKey as K] = (
          <div className={config.formItemClassName}>
            <FormLabel className="text-base font-normal">{renderLabel(config.label)}</FormLabel>
          </div>
        );
      })
      .with({ type: "ranking" }, (config) => {
        formComponents[configKey as K] = (
          <FormField
            control={form.control}
            name={configKey as any}
            render={({ field }) => (
              <FormItem className={cn(config.formItemClassName)}>
                {config.label && <FormLabel>{renderLabel(config.label)}</FormLabel>}
                {config.description && <FormDescription>{config.description}</FormDescription>}
                <FormControl>
                  <RankingInput {...field} items={config.items} />
                </FormControl>
                {config.helpText && <P3 gray>{config.helpText}</P3>}
                <FormMessage />
              </FormItem>
            )}
          />
        );
      })
      .exhaustive();
  }

  //
  // get the form components
  //
  Object.entries(config).forEach(([key, value]) => {
    //
    // if this is an object, we need to go in!
    //
    if (value.type === "object") {
      //
      // if all values in the object are "not_applicable", then we check the checkbox
      //
      const allValuesAreNotApplicable = watchedFields
        .filter((field) => field.startsWith(`${key}.`))
        .every((field, index) => watchedValues[index] === "not_applicable");

      formComponents[key as K] = (
        <div className="-mb-4">
          {/* // (maybe) add a label, if it is not empty // */}
          {value.label && <FormLabel>{value.label}</FormLabel>}
          {value.offerNotApplicableForEntireObject && (
            <div className="mt-3 flex items-start space-x-3 space-y-0">
              <Checkbox
                id={`${key}NotApplicableGroup`}
                checked={allValuesAreNotApplicable}
                onCheckedChange={(checked) => {
                  // field.onChange(checked ? "not_applicable" : "");
                  Object.entries(value.properties).forEach(([keyInObject, valueInObject]) => {
                    form.setValue(`${key}.${keyInObject}`, checked ? "not_applicable" : null);
                  });
                }}
              />

              <div className="space-y-1 leading-none">
                <Label htmlFor={`${key}NotApplicableGroup`}>{l("Not Applicable", "Ne s'applique pas")}</Label>
              </div>
            </div>
          )}
        </div>
      );

      //
      // get the inner form components
      //
      Object.entries(value.properties).forEach(([keyInObject, valueInObject]) => {
        getFormComponent({
          configKey: `${key}.${keyInObject}`,
          config: valueInObject,
          objectHasNotApplicableOption: !!value.offerNotApplicableForEntireObject,
          objectHasNotApplicableChecked: allValuesAreNotApplicable,
        });
      });
    } else {
      getFormComponent({
        configKey: key,
        config: value,
      });
    }
  });

  // console.log(formComponents);

  return formComponents;
}

function NotApplicableCheckbox({ field, configKey }: { field: ControllerRenderProps<any, any>; configKey: string }) {
  const { l } = useI18n();

  return (
    <div className="flex items-start space-x-3 space-y-0">
      <Checkbox
        id={`${configKey}NotApplicable`}
        checked={field.value === "not_applicable"}
        onCheckedChange={(checked) => {
          field.onChange(checked ? "not_applicable" : "");
        }}
      />
      <div className="space-y-1 leading-none">
        <Label htmlFor={`${configKey}NotApplicable`}>{l("Not Applicable", "Ne s'applique pas")}</Label>
      </div>
    </div>
  );
}
